import { useMemo, useState } from "react";
import { ConfirmedAppointment } from "../AppointmentConfirmedScreen/parseConfirmedAppointment";
import { BookingPackage } from "../AppointmentBookingContext/useBookingPackages";

export type SuggestedPackage = {
  centerPackage: BookingPackage;
  forOfferings: string[];
  isSelected: boolean;
  isDisabled: boolean;
  priceDiff: number;
  upgradeOfferingPrice: number;
};

const useSuggestedPackages = ({
  appointmentData,
  centerPackages,
}: {
  appointmentData: ConfirmedAppointment;
  centerPackages?: BookingPackage[] | null;
}): {
  suggestedPackages: SuggestedPackage[];
  onSelect: (packageId: string) => void;
  onDeselect: (packageId: string) => void;
} | null => {
  const [selectedPackages, setSelectedPackages] = useState<{
    [key: string]: boolean;
  }>({});

  const suggestedPackages = useMemo<SuggestedPackage[] | null>(() => {
    if (!centerPackages) return null;

    const selectedOfferingIds = appointmentData.offerings
      .map((offering) => offering.globalOfferingId)
      .filter(Boolean);

    const selectedPackageOfferingIds = new Set<string>();

    Object.keys(selectedPackages).forEach((packageId) => {
      if (selectedPackages[packageId]) {
        const selectedPackage = centerPackages.find(
          (pkg) => pkg.id === packageId,
        );
        if (selectedPackage) {
          selectedPackage.globalStudyIds.forEach((id) =>
            selectedPackageOfferingIds.add(id),
          );
        }
      }
    });

    return centerPackages
      .map((centerPackage) => {
        const packageOfferingIds = centerPackage.globalStudyIds;
        const matchedOfferings = packageOfferingIds.filter((id: string) =>
          selectedOfferingIds.includes(id),
        );

        const isPurchased = appointmentData.packages.some(
          (pkg) => pkg.centerPackageId === centerPackage.id,
        );

        const shouldSuggest =
          packageOfferingIds.length > 1 &&
          (matchedOfferings.length === packageOfferingIds.length ||
            matchedOfferings.length === packageOfferingIds.length - 1);

        if (!shouldSuggest || isPurchased) {
          return null;
        }

        const isSelected = selectedPackages[centerPackage.id];

        const hasOverlappingOfferings = packageOfferingIds.some((offeringId) =>
          selectedPackageOfferingIds.has(offeringId),
        );

        const isDisabled = hasOverlappingOfferings && !isSelected;

        const offerings = appointmentData.offerings.filter(
          (appointmentOffering) =>
            packageOfferingIds.includes(appointmentOffering.globalOfferingId),
        );

        const offeringsPrice = offerings.reduce(
          (acc, offering) => acc + offering.price,
          0,
        );

        const upgradeOfferings = centerPackage.offerings?.filter(
          (offering) => !matchedOfferings.includes(offering.offeringId),
        );

        const upgradeOfferingPrice = upgradeOfferings?.reduce(
          (acc, offering) => acc + offering.price,
          0,
        );

        const priceDiff = centerPackage.price - offeringsPrice;

        if (priceDiff <= 0) {
          return null;
        }

        return {
          centerPackage,
          forOfferings: matchedOfferings,
          isSelected,
          isDisabled,
          upgradeOfferingPrice,
          priceDiff: priceDiff > 0 ? priceDiff : 0,
        };
      })
      .filter(Boolean) as SuggestedPackage[];
  }, [centerPackages, selectedPackages, appointmentData]);

  if (!centerPackages || suggestedPackages === null) return null;

  const onSelect = (packageId: string) => {
    setSelectedPackages((prev) => ({ ...prev, [packageId]: true }));
  };

  const onDeselect = (packageId: string) => {
    setSelectedPackages((prev) => ({ ...prev, [packageId]: false }));
  };

  return { suggestedPackages, onSelect, onDeselect };
};

export default useSuggestedPackages;
